import { useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLoaderData } from 'react-router';

import { useQuotationForClient } from '~/api/quotations';
import { Card, ExternalLink, InfoBlock, LegacyRichTextRenderer, Separator, Text, Title } from '~/components';
import { creativeShelter } from '~/constants/creativeShelter';
import { useIntl } from '~/hooks/useIntl';
import { useLanguageContext } from '~/providers/LanguageProvider/LanguageProvider.context';

import type { loader } from './QuotationForClient.loader';

import {
  asideStyle,
  contentStyle,
  creatorCardStyle,
  gridStyle,
  mutedTextStyle,
  pageStyle,
  partiesStyle,
  partyCardStyle,
  partyDetailsStyle,
  partyLogoStyle,
  partyNameStyle,
  preLineStyle,
  richTextStyle,
  sectionStyle,
} from './QuotationForClient.css';
import { QuotationForClientStatusCard } from './QuotationForClientStatusCard';
import { QuotationForClientTotals } from './QuotationForClientTotals';
import { QuotationLinesTable } from './QuotationLinesTable';

export const QuotationForClient = () => {
  const { approvalKey, quotationForClient: loaderData } = useLoaderData<typeof loader>();
  const { data: quotationForClient = loaderData } = useQuotationForClient(approvalKey);
  const { client, quotation, user } = quotationForClient;

  const { language, setLanguage } = useLanguageContext();
  const { formatDate } = useIntl(client.language);
  const { t } = useTranslation(['common', 'quotations']);

  useLayoutEffect(() => {
    if (client.language !== language) {
      setLanguage(client.language);
    }
  }, [client.language, language, setLanguage]);

  return (
    <div className={pageStyle}>
      <Title as="h1" eyebrow={t('common:models.quotation')}>
        {quotation.title}
      </Title>

      <div className={gridStyle}>
        <aside className={asideStyle}>
          <QuotationForClientStatusCard approvalKey={approvalKey} quotationForClient={quotationForClient} />

          <Card.Root className={creatorCardStyle} size="small">
            <Text className={mutedTextStyle}>{t('quotations:clientAccept.preview.createdBy')}</Text>

            <div className={partyDetailsStyle}>
              <span className={partyNameStyle}>{creativeShelter.companyName}</span>
              <span>{creativeShelter.addressLine1}</span>
              <span>{creativeShelter.addressLine2}</span>
              <span>{creativeShelter.vatNumber}</span>
              <span>{creativeShelter.iban}</span>
              <ExternalLink href="mailto:info@creativeshelter.be" target="_self">
                info@creativeshelter.be
              </ExternalLink>
              <ExternalLink href="https://www.creativeshelter.be">creativeshelter.be</ExternalLink>
            </div>
          </Card.Root>
        </aside>

        <div className={contentStyle}>
          <InfoBlock label={t('quotations:clientAccept.preview.expirationDate')} variant="caps">
            <Text>{formatDate(quotation.expirationDate)}</Text>
          </InfoBlock>

          <div className={partiesStyle}>
            <InfoBlock label={t('quotations:clientAccept.preview.client')} variant="caps">
              <Card.Root className={partyCardStyle} size="small" variant="primary-accent">
                <div className={partyDetailsStyle}>
                  <span className={partyNameStyle}>{client.name}</span>
                  <span>
                    {client.address.street} {client.address.number}
                  </span>
                  <span>
                    {client.address.zipCode} {client.address.city}
                  </span>
                  {(client.vatNumber ?? client.otherNumber) && <span>{client.vatNumber ?? client.otherNumber}</span>}
                </div>
              </Card.Root>
            </InfoBlock>

            <InfoBlock label={t('quotations:clientAccept.preview.freelancer')} variant="caps">
              <Card.Root className={partyCardStyle} size="small" variant="primary-accent">
                <div className={partyDetailsStyle}>
                  <span className={partyNameStyle}>{user.companyName}</span>
                  {user.fullName && <span>{user.fullName}</span>}
                  <span>
                    {user.address.street} {user.address.number}
                  </span>
                  <span>
                    {user.address.zipCode} {user.address.city}
                  </span>
                  <span>{user.vatNumber}</span>
                </div>

                {user.logoUrl && <img alt={`Logo ${user.companyName}`} className={partyLogoStyle} src={user.logoUrl} />}
              </Card.Root>
            </InfoBlock>
          </div>

          <Separator />

          <section className={sectionStyle}>
            <Title as="h2" styleAs="h3">
              {t('quotations:clientAccept.preview.introduction')}
            </Title>

            <div className={richTextStyle}>
              <LegacyRichTextRenderer content={quotation.introduction} />
            </div>
          </section>

          <section className={sectionStyle}>
            <Title as="h2" styleAs="h3">
              {t('quotations:clientAccept.preview.description')}
            </Title>

            <div className={richTextStyle}>
              <LegacyRichTextRenderer content={quotation.description} />
            </div>
          </section>

          <Separator />

          <section className={sectionStyle}>
            <Title as="h2" styleAs="h3">
              {t('quotations:clientAccept.preview.assignment.title')}
            </Title>

            <QuotationLinesTable language={client.language} lines={quotation.quotationLines} />

            <QuotationForClientTotals calculationData={quotation.calculationData} language={client.language} />
          </section>

          {(quotation.conditionUrl || quotation.specialConditions) && <Separator />}

          {quotation.conditionUrl && (
            <section className={sectionStyle}>
              <Title as="h2" styleAs="h3">
                {t('quotations:clientAccept.preview.conditions.title')}
              </Title>

              <Text>
                <ExternalLink href={quotation.conditionUrl}>{t('quotations:clientAccept.preview.conditions.description')}</ExternalLink>
              </Text>
            </section>
          )}

          {quotation.specialConditions && (
            <section className={sectionStyle}>
              <Title as="h2" styleAs="h3">
                {t('quotations:clientAccept.preview.specialConditions')}
              </Title>

              <Text className={preLineStyle}>{quotation.specialConditions}</Text>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};
